import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { DatePicker } from '../components/ui/DatePicker';
import { Checkbox } from '../components/ui/Checkbox';
import { CustomerSelect } from '../components/domain/CustomerSelect';
import { LineItemRow } from '../components/domain/LineItemRow';
import { TotalsPanel } from '../components/domain/TotalsPanel';
import { calculateSubtotal, calculateTaxAmount, calculateInvoiceTotal, calculateProfitMargin, addDays } from '../utils/calculations';
import { LineItem, SubscriptionStatus } from '../types';
import { ArrowLeftIcon, PlusIcon, SaveIcon, AlertCircleIcon } from 'lucide-react';
interface LineItemFormData {
  description: string;
  quantity: number;
  price: number;
  jobCost?: number;
  isTaxable: boolean;
}
export function SubscriptionForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const isEditing = !!id;
  const {
    createSubscription,
    updateSubscription,
    getSubscriptionById,
    selectedCompanyId,
    taxRate
  } = useData();
  const [isLoading, setIsLoading] = useState(false);
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [cycleDays, setCycleDays] = useState(30);
  const [status, setStatus] = useState<SubscriptionStatus>('active');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [lineItems, setLineItems] = useState<LineItemFormData[]>([{
    description: '',
    quantity: 1,
    price: 0,
    isTaxable: true
  }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Load existing subscription data
  useEffect(() => {
    if (isEditing && id) {
      const subscription = getSubscriptionById(id);
      if (subscription) {
        setCustomerId(subscription.customer_id);
        setStartDate(new Date(subscription.start_date).toISOString().split('T')[0]);
        setCycleDays(subscription.cycle_days);
        setStatus(subscription.status);
        setTermsAccepted(subscription.terms_accepted);
      }
    }
  }, [isEditing, id, getSubscriptionById]);
  // Calculate totals
  const calculatedLineItems: LineItem[] = useMemo(() => {
    return lineItems.map((item, index) => ({
      id: `temp-${index}`,
      invoice_id: 'new',
      description: item.description,
      quantity: item.quantity,
      price: item.price,
      job_cost: item.jobCost,
      is_taxable: item.isTaxable,
      profit_margin: calculateProfitMargin(item.price, item.jobCost)
    }));
  }, [lineItems]);
  const subtotal = useMemo(() => calculateSubtotal(calculatedLineItems), [calculatedLineItems]);
  const taxAmount = useMemo(() => calculateTaxAmount(calculatedLineItems, taxRate), [calculatedLineItems, taxRate]);
  const total = useMemo(() => calculateInvoiceTotal(subtotal, taxAmount, 0, 0), [subtotal, taxAmount]);
  const nextBillingDate = useMemo(() => {
    return addDays(new Date(startDate), cycleDays);
  }, [startDate, cycleDays]);
  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
  };
  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };
  const updateLineItem = (index: number, updates: Partial<LineItemFormData>) => {
    setLineItems(lineItems.map((item, i) => i === index ? {
      ...item,
      ...updates
    } : item));
  };
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!customerId) {
      newErrors.customer = 'Please select a customer';
    }
    if (!startDate) {
      newErrors.startDate = 'Please select a start date';
    }
    if (!termsAccepted) {
      newErrors.terms = 'Customer must accept terms';
    }
    if (lineItems.length === 0) {
      newErrors.lineItems = 'Please add at least one line item';
    }
    const hasEmptyLineItem = lineItems.some(item => !item.description.trim() || item.price <= 0);
    if (hasEmptyLineItem) {
      newErrors.lineItems = 'Please fill in all line item details';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      const subscriptionData = {
        company_id: selectedCompanyId,
        customer_id: customerId,
        start_date: new Date(startDate),
        next_billing_date: nextBillingDate,
        cycle_days: cycleDays,
        terms_accepted: termsAccepted,
        status
      };
      const lineItemsData = lineItems.map(item => ({
        description: item.description,
        quantity: item.quantity,
        price: item.price,
        job_cost: item.jobCost,
        is_taxable: item.isTaxable,
        profit_margin: calculateProfitMargin(item.price, item.jobCost)
      }));
      if (isEditing && id) {
        await updateSubscription(id, subscriptionData);
      } else {
        await createSubscription(subscriptionData, lineItemsData);
      }
      navigate('/subscriptions');
    } catch (error) {
      console.error('Failed to save subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const cycleOptions = [{
    value: '7',
    label: 'Weekly'
  }, {
    value: '14',
    label: 'Bi-weekly'
  }, {
    value: '30',
    label: 'Monthly'
  }, {
    value: '60',
    label: 'Bi-monthly'
  }, {
    value: '90',
    label: 'Quarterly'
  }, {
    value: '180',
    label: 'Semi-annually'
  }, {
    value: '365',
    label: 'Annually'
  }];
  const statusOptions = [{
    value: 'active',
    label: 'Active'
  }, {
    value: 'paused',
    label: 'Paused'
  }, {
    value: 'canceled',
    label: 'Canceled'
  }];
  return <div className="space-y-6" data-id="element-445">
      {/* Page Header */}
      <div className="flex items-center gap-4" data-id="element-446">
        <Button variant="ghost" onClick={() => navigate('/subscriptions')} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-448" />} data-id="element-447">
          Back
        </Button>
        <div className="flex-1" data-id="element-449">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-450">
            {isEditing ? 'Edit Subscription' : 'New Subscription'}
          </h1>
        </div>
      </div>

      {/* Auto-generate Invoice Notice */}
      {!isEditing && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-451">
          <AlertCircleIcon className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-452" />
          <div data-id="element-453">
            <p className="text-sm font-medium text-blue-900" data-id="element-454">
              First Invoice Auto-Generated
            </p>
            <p className="text-sm text-blue-700 mt-1" data-id="element-455">
              When you create this subscription, the first invoice will be
              automatically generated and sent to the customer.
            </p>
          </div>
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-456">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6" data-id="element-457">
          {/* Subscription Details */}
          <Card data-id="element-458">
            <CardHeader title="Subscription Details" data-id="element-459" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-460">
              <CustomerSelect value={customerId} onChange={setCustomerId} error={errors.customer} data-id="element-461" />
              <DatePicker label="Start Date" value={startDate} onChange={e => setStartDate(e.target.value)} error={errors.startDate} data-id="element-462" />
              <Select label="Billing Cycle" options={cycleOptions} value={cycleDays.toString()} onChange={e => setCycleDays(parseInt(e.target.value))} data-id="element-463" />
              {isEditing && <Select label="Status" options={statusOptions} value={status} onChange={e => setStatus(e.target.value as SubscriptionStatus)} data-id="element-464" />}
            </div>
            <div className="mt-4" data-id="element-465">
              <Checkbox label="Customer has accepted terms and conditions" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} error={errors.terms} data-id="element-466" />
            </div>
          </Card>

          {/* Line Items */}
          {!isEditing && <Card data-id="element-467">
              <CardHeader title="Recurring Items" subtitle="These items will be billed each cycle" action={<Button variant="secondary" size="sm" onClick={addLineItem} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-470" />} data-id="element-469">
                    Add Item
                  </Button>} data-id="element-468" />
              {errors.lineItems && <p className="text-sm text-red-600 mb-4" data-id="element-471">{errors.lineItems}</p>}
              <div className="space-y-4" data-id="element-472">
                {lineItems.map((item, index) => <LineItemRow key={index} index={index} description={item.description} quantity={item.quantity} price={item.price} jobCost={item.jobCost} isTaxable={item.isTaxable} onDescriptionChange={value => updateLineItem(index, {
              description: value
            })} onQuantityChange={value => updateLineItem(index, {
              quantity: value
            })} onPriceChange={value => updateLineItem(index, {
              price: value
            })} onJobCostChange={value => updateLineItem(index, {
              jobCost: value
            })} onTaxableChange={value => updateLineItem(index, {
              isTaxable: value
            })} onRemove={() => removeLineItem(index)} canRemove={lineItems.length > 1} data-id="element-473" />)}
              </div>
            </Card>}
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-474">
          {/* Billing Info */}
          <Card data-id="element-475">
            <CardHeader title="Billing Schedule" data-id="element-476" />
            <div className="space-y-3 text-sm" data-id="element-477">
              <div className="flex justify-between" data-id="element-478">
                <span className="text-gray-500" data-id="element-479">Start Date</span>
                <span className="font-medium text-gray-900" data-id="element-480">
                  {new Date(startDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-481">
                <span className="text-gray-500" data-id="element-482">Billing Cycle</span>
                <span className="font-medium text-gray-900" data-id="element-483">
                  {cycleOptions.find(o => o.value === cycleDays.toString())?.label}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-484">
                <span className="text-gray-500" data-id="element-485">Next Billing</span>
                <span className="font-medium text-gray-900" data-id="element-486">
                  {nextBillingDate.toLocaleDateString()}
                </span>
              </div>
            </div>
          </Card>

          {/* Totals */}
          {!isEditing && <Card data-id="element-487">
              <CardHeader title="Per-Cycle Amount" data-id="element-488" />
              <TotalsPanel subtotal={subtotal} taxAmount={taxAmount} taxRate={taxRate} total={total} data-id="element-489" />
            </Card>}

          {/* Payment Method */}
          <Card data-id="element-490">
            <CardHeader title="Payment Method" data-id="element-491" />
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-492">
              <p className="text-sm text-gray-500 mb-2" data-id="element-493">
                Saved Card (Test Mode)
              </p>
              <div className="flex items-center gap-3" data-id="element-494">
                <div className="w-10 h-6 bg-blue-600 rounded flex items-center justify-center text-white text-xs font-bold" data-id="element-495">
                  VISA
                </div>
                <span className="text-sm text-gray-900" data-id="element-496">•••• 4242</span>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <Card data-id="element-497">
            <Button className="w-full" onClick={handleSave} isLoading={isLoading} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-499" />} data-id="element-498">
              {isEditing ? 'Save Changes' : 'Create Subscription'}
            </Button>
          </Card>
        </div>
      </div>
    </div>;
}